'use client';

import classNames from 'classnames';
import React, { useEffect, useState, FocusEvent, useId } from 'react';

// Types
import { TextProps } from '../../types/controls/text';

// Contexts
import { useFormContext } from '../../contexts/FormProvider';

// Services
import { validate } from '../../services/controls/text';

// Components
import { Box } from '../Container/Box';
import { HelpMessage } from '../Display/HelpMessage';
import { ValidationMessage } from '../Display/ValidationMessage';

// Static Content
import content from '../../content/content.json';

export const Text = ({
  id,
  name,
  style,
  label,
  state,
  margin,
  onBlur,
  onClick,
  onKeyUp,
  isInline,
  dataType,
  onChange,
  disabled,
  readOnly,
  baseWidth,
  ariaLabel,
  inputSize,
  forceReset,
  placeholder,
  helpMessage,
  setValidate,
  controlWidth,
  autoComplete,
  type = 'text',
  validationRules,
  validate: triggerValidation,
}: TextProps) => {
  const uniqueId = useId();
  const componentId = id ?? uniqueId;

  const { validationMessages } = useFormContext();

  const [remainingCharacters, setRemainingCharacters] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const onInputChange = (value: string) => {
    const validationMessage = validate({
      value: value,
      ruleSet: validationRules,
      validationMessages: validationMessages,
    });

    onChange({ value: value, isValid: !validationMessage });
  };

  const onInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    event.preventDefault();

    onBlur?.(event);

    validateWithMessages();
  };

  const validateWithMessages = () => {
    const validationMessage = validate({
      value: state.value,
      ruleSet: validationRules,
      validationMessages: validationMessages,
    });

    setValidate?.(false);
    setErrorMessage(validationMessage);
  };

  useEffect(() => {
    if (!triggerValidation) {
      return;
    }

    validateWithMessages();
  }, [triggerValidation]);

  useEffect(() => {
    if (!forceReset) {
      return;
    }

    const newValue = '';

    const validationMessage = validate({
      value: newValue,
      ruleSet: validationRules,
      validationMessages: validationMessages,
    });

    setErrorMessage(undefined);
    onChange({ value: newValue, isValid: !validationMessage });
  }, [forceReset]);

  // Purpose  : Will silently validate the component as part of first load so
  //            the `isValid` state is correctly configured.
  useEffect(() => {
    const validationMessage = validate({
      value: state.value,
      ruleSet: validationRules,
      validationMessages: validationMessages,
    });

    onChange({ value: state.value, isValid: !validationMessage });
  }, []);

  useEffect(() => {
    if (validationRules?.maxLength === undefined) {
      return;
    }

    setRemainingCharacters(
      validationRules.maxLength - state.value.toString().length,
    );
  }, [state.value]);

  return (
    <Box
      component="div"
      margin={margin}
      width={baseWidth}
      data-type={isInline ? 'inline' : undefined}
      className={classNames('fieldset-item', {
        'm-0': isInline,
        'is-negative': errorMessage,
      })}
    >
      {label && (
        <label
          htmlFor={componentId}
          className={classNames({
            disabled: disabled,
            repel: validationRules?.maxLength,
          })}
        >
          {label}
          {!validationRules?.required && (
            <small>&nbsp;{content.optional}</small>
          )}

          {validationRules?.maxLength !== undefined && (
            <small>
              {remainingCharacters}/{validationRules.maxLength}
            </small>
          )}
        </label>
      )}

      <input
        type={type}
        name={name}
        style={style}
        id={componentId}
        onKeyUp={onKeyUp}
        onClick={onClick}
        disabled={disabled}
        readOnly={readOnly}
        value={state.value}
        onBlur={onInputBlur}
        data-type={dataType}
        data-size={inputSize}
        placeholder={placeholder}
        autoComplete={autoComplete}
        maxLength={validationRules?.maxLength}
        minLength={validationRules?.minLength}
        aria-required={validationRules?.required}
        aria-label={ariaLabel ? ariaLabel : undefined}
        onChange={(event) => onInputChange(event.target.value)}
        className={classNames({ [`${controlWidth}`]: controlWidth })}
      />

      {helpMessage && <HelpMessage>{helpMessage}</HelpMessage>}
      {errorMessage && <ValidationMessage>{errorMessage}</ValidationMessage>}
    </Box>
  );
};
